<template>
  <div id="estimation" class="pt-2 pb-5">

    <div v-if="!firstLoad" class="estimation__info columns is-multiline">

      <div
        v-if="(!estimation.rfq.is_accepted || (
            estimation.rfq.drawing_highlights.length > 0 && 
            !estimation.rfq.drawing_highlights[0].is_approved)) && 
            !estimation.is_completed" 
        class="column is-full">
        <v-alert 
          type="danger">
          <span v-if="!estimation.rfq.is_accepted">
            This estimation cannot be started because the RFQ has not been
            approved by an estimator.
          </span>
          <span v-else>
            This estimation cannot be started because the 
            drawing highlight has not been approved.
          </span>
        </v-alert>
      </div>

      <div class="column is-8 flex flex--row flex--align-start px-2 py-0">
        <div class="column is-3">
          <label class="help">Project</label>
          <div class="subtitle is-6 py-2">
            {{ estimation.rfq.project.project_name }}
          </div>
        </div>
        
        <div class="column">
          <label class="help">RFQ</label>
          <div class="subtitle is-6 py-2">
            <router-link 
              :to="{ 
                name: 'RfqViewer', 
                params: { 
                  rfqId: estimation.rfq.id,
                  productBrand: 'Mitrex'
                } 
              }">
              {{ estimation.rfq.rfq_name }}
            </router-link>
          </div>
        </div>

        <div class="column is-2">
          <label class="help">Estimation Status</label>
          <div class="subtitle is-6 py-2 is-capitalized">
            {{ estimation.status.toLowerCase() }}
          </div>
        </div>

        <div class="column is-2">
          <label class="help">Main Estimator</label>
          <v-select
            class="mt-1"
            is-small
            v-if="users.length > 0"
            required
            :disabled="!editable"
            item-text="display_name"
            item-value="id"
            :options="users.filter(d => ['estimator', 'sales-estimator', , 'sales-estimator-samples'].includes(d.role))"
            v-model="estimation.rfq.main_estimator_id"
            @input="changeEstimator($event)"
          />
        </div>

        <div class="column is-2" v-if="estimation.rfq.rfq_type !== CONDENSED">
          <label class="help">Viewing Pricing Type</label>
          <div class="flex flex--column py-2">
            <label class="radio">
              <input 
                type="radio" 
                name="mpa pricing" 
                @input="setSummaryParams({ field: 'pricing_type', value: 'mpa' })"
                :checked="summaryParams.pricing_type == 'mpa'">
              MPA Pricing
            </label>
            <label class="radio ml-0 mt-1">
              <input 
                type="radio" 
                name="dpa pricing" 
                @input="setSummaryParams({ field: 'pricing_type', value: 'dpa' })"
                :checked="summaryParams.pricing_type == 'dpa'">
              DPA Pricing
            </label>
          </div>
        </div>
      </div>

      <div class="column is-4 flex flex--align-start buttons flex--justify-end pt-4">

        <button
          v-if="!firstLoad && 
            !estimation.is_ready_for_pricing && 
            can.markAsReadyForPricing"
          @click="confirmReadyForPricing()"
          :disabled="!editable"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Mark as Ready for Pricing
          </span>
        </button>

        <!-- <button
          v-if="!firstLoad && estimation.is_completed && !estimation.is_director_approved"
          @click="confirmDirectorApproval()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Approved by Director
          </span>
        </button> -->

        <button
          v-if="!firstLoad && 
            estimation.is_ready_for_pricing && 
            !estimation.is_completed && can.markAsCompleted"
          @click="confirmCompleted()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Mark as Complete
          </span>
        </button>

        <button
          v-if="
            !firstLoad && 
            estimation.is_ready_for_pricing &&
            estimation.is_completed && 
            !estimation.is_client_approved"
          @click="duplicatingRfq = true"
          class="button">
          <span class="icon is-medium">
            <i class="fas fa-copy"></i>
          </span>
          <span>              
            Create New Version
          </span>
        </button>

        <!-- <button
          v-if="
            !firstLoad && 
            estimation.is_ready_for_pricing &&
            estimation.is_completed && 
            !estimation.is_client_approved"
          @click="confirmClientApproval()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Approved
          </span>
        </button> -->

        <v-fade-transition mode="out-in">
          <button 
            v-if="!firstLoad && 
              !estimation.is_ready_for_pricing && 
              estimation.rfq.rfq_type !== CONDENSED"
            class="button is-link" 
            :class="{ 'is-loading': saving }"
            :disabled="saving || !editable"
            @click.stop="saveChanges()"
          >
            <span class="icon is-medium">
              <i class="fas fa-save"></i>
            </span>
            <span>              
              Save and Refresh
            </span>
          </button>
        </v-fade-transition>

        <button
          v-if="editable"
          @click="adjustingParameters = true" 
          class="button">
          <span class="icon is-small">
            <i class="fas fa-cogs"></i>
          </span>
        </button>
      </div>           
    </div>

    <estimation-condensed
      v-if="estimation && estimation.rfq.rfq_type === CONDENSED"
      ref="condensedEstimation"
      :editable="editable"
      :update="update"
      :updateInsulationParam="updateInsulationParam"
    />

    <estimation-full-mitrex 
      v-if="estimation && estimation.rfq.rfq_type !== CONDENSED"
      :editable="editable"
      :update="update"
      :adjustPrice="adjustPrice"
      :updateInsulationParam="updateInsulationParam"
      :can="can"
      :setSaving="setSaving"
      ref="fullEstimation"
    />

    <v-confirm-modal
      v-model="confirmModal.state"
      :title="confirmModal.title"
      :type="confirmModal.type"
      :message="confirmModal.message"
      :pending="confirmModal.pending"
      @confirm="confirmModal.confirmCb"/>

    <adjust-parameters-modal 
      v-if="!firstLoad"
      :estimation="estimation"
      :show-params="showParams"
      v-model="adjustingParameters"
      @save-changes="saveEstimationParams"
      :saving.sync="savingParams"/>

    <duplicate-rfq-modal
      v-if="!firstLoad"
      v-model="duplicatingRfq"
      :rfq="estimation.rfq"
      :duplicate-estimation="true"/>

    <v-loading 
      :value="firstLoad || summaryLoading || saving || savingSummary || updatingInsulation"
      :message="loadingMessage">
    </v-loading>

  </div>
</template>

<script>
import VConfirmModal from '@/components/base/VConfirmModal'
import VSelect from '@/components/base/VSelect'
import VLoading from '@/components/base/VLoading'
import VAlert from '@/components/base/VAlert'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import EstimationCondensed from '@/components/estimations/EstimationCondensed'
import EstimationFullMitrex from '@/components/estimations/EstimationFullMitrex'
import AdjustParametersModal from '@/components/estimations/AdjustParametersModal'

import DuplicateRfqModal from '@/components/rfq/DuplicateRfqModal'
import { CONDENSED, FULL } from '@/helpers/constants'

import { newEstimationHelpers, commonEstimationHelpers } from '@/mixins/helpers'
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VConfirmModal, AdjustParametersModal, DuplicateRfqModal, 
    VSelect, VLoading, VAlert, VFadeTransition, EstimationFullMitrex,
    EstimationCondensed
  },
  mixins: [newEstimationHelpers, commonEstimationHelpers],
  data() {
    return {
      CONDENSED: CONDENSED,
      FULL: FULL,
      detailWidth: '500',
      adjustingParameters: false,
      confirmModal: {
        state: false,
        title: 'Confirm mark as complete',
        message: 'Are you sure you want to mark this estimation as complete? \
        Once marked as complete, you will not be able make more edits \
        unless a revision is requested ',
        type: 'warning',
        pending: false,
        confirmCb: () => {}
      },
      saving: false,
      savingParams: false,
      savingSummary: false,
      duplicatingRfq: false,
      updatingInsulation: false,
      batteryMap: {
        'system_size': 'batteries_system_size_kw',
        'average_value': 'batteries_and_invertors_val'
      },
      showParams: [
        {
          name: 'Solar Power Calculation Parameters',
          fields: [
            "cell_width", "cell_distance", "string_distance",
            "side_margin", "top_margin", "bottom_margin", "top_busbar_space",
            "bottom_busbar_space", "busbar_thickness"
          ]
        },
        {
          name: 'Item Market Value',
          fields: [
            "solar_wall_active_val", "solar_wall_nonactive_val",
            "cladding_val", "railing_1100_1400_val", "irregular_val",
            "batteries_and_invertors_val"
          ]
        },
        {
          name: 'Electricity Pricing',
          fields: [
            "elec_for_mitrex", "elec_for_mitrex_dpa", "elec_for_owner", 
            "elec_for_owner_dpa"
          ]
        },
        {
          name: 'Tax Incentives',
          fields: [
            "alternative_material_cost_wall", "alternative_material_cost_railing",
            "cap_rate", "tax_rate", "dep_time", "maintenance_cost_wall",
            "maintenance_cost_railing"
          ]
        }
      ]
    }
  },
  computed: {
    ...mapState("cladifyEstimation", [
      "estimation", "loading", "firstLoad",
      "estimationSummary", "summaryLoading", "summaryFirstLoad", "summaryParams"
    ]),
    ...mapState("users", ["users"]),
    ...mapState("elevations", {
      newElevations: "elevations"
    }),
    ...mapState("auth", ["user"]),
    can({ user }) {
      return {
        markAsReadyForPricing: ["ceo", "estimator", "sales-estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin',
        markAsCompleted: ["ceo", "sales", "sales-estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin'
      }
    },
    loadingMessage() {
      if (this.savingSummary) {
        return "Just a second, we're updating the estimation ..."
      } else if (this.saving) {
        return "Saving estimation details and recalculating summaries ..."
      } else if (this.firstLoad || this.summaryLoading) {
        return "Loading estimation, this may take a few seconds ..."
      } else if (this.updatingInsulation) {
        return "Updating Insulation ..."
      } else {
        return null
      }
    },
    editable({ estimation }) {
      return estimation?.rfq?.is_accepted && 
        estimation?.rfq?.drawing_highlights[0] &&
        estimation?.rfq?.drawing_highlights[0].is_approved && 
        !estimation?.is_completed
    }
  },
  watch: {
    summaryParams: {
      handler() {
        this.getSummary(this.estimation.id);
      },
      deep: true
    },
    '$route.params.estimationId'(id) {
      this.initialize();
    }
  },
  mounted() {
    this.initialize();
  },
  methods: {
    ...mapActions("materials", ["getMaterials"]),
    ...mapActions("cladifyEstimation", [
      "getEstimationSummary","getEstimation", "updateEstimation", 
      "updateEstimator", "adjustSummaryPrice", "updateInsulation"
    ]),
    ...mapActions("users", ["getUsers"]),
    ...mapMutations("cladifyEstimation", [
      "setEstimation", "setLoading", "setFirstLoad", "setSummaryParams",
      "setNewSummary"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      this.setLoading(true);
      let estimation = this.$route.params.estimation;
      if (estimation) {
        this.setEstimation(estimation);
        this.setUpEstimation();
        this.setLoading(false);
        this.setFirstLoad(false);
      } else {
        this.getEstimation(this.$route.params.estimationId).then(() => {
          this.setUpEstimation();
        })
      }

      this.getUsers({ per_page: 100 });
    },
    setUpEstimation() {
      if (this.estimation.rfq.rfq_type !== CONDENSED) {
        this.getSummary(this.$route.params.estimationId);
        this.$refs.fullEstimation.initializeEstimation();
      } else {
        this.$refs.condensedEstimation.initializeEstimation();
      }
    },
    getSummary() {
      this.getEstimationSummary({ 
        id: this.$route.params.estimationId,
        params: this.summaryParams 
      }).then(resp => {
        this.setNewSummary(resp.data);
      })
    },
    changeEstimator($event) {
      this.updateEstimator({
        rfqId: this.estimation.rfq.id,
        data: { main_estimator_id: $event }
      })
    },
    update(field, value, refreshSummary=false, replaceWithResponse=true) {
      this.savingParams = true;
      let payload = {}
      payload[field] = value;

      this.updateEstimation({
        estimationId: this.estimation.id,
        data: payload,
        replaceWithResponse
      }).then(() => {
        if (refreshSummary) {
          this.getSummary(this.estimation.id);
        }
        this.savingParams = false;
      }).catch(() => this.savingParams = false);
    },
    confirmReadyForPricing() {
      const workingFromDraft = this.estimation.rfq.rfq_type !== CONDENSED
        ? this.$refs.fullEstimation.workingFromDraft
        : false

      this.confirmModal.confirmCb = () => {
        this.confirmModal.pending = true;
        if (workingFromDraft) {
          this.saveChanges().then(() => {
            this.confirmModal.pending = false;
            this.markAsReadyForPricing();
          }).catch(() => this.confirmModal.pending = false)
        } else {
          this.markAsReadyForPricing();
        }
      }
      let warning = "";
      if (workingFromDraft) {
        warning = `You are currently working on an unsaved draft.<br/>
          <strong>Marking this estimation as ready for pricing will automatically save
          the current draft.</strong><br/>`
      }
      this.confirmModal.title = "Confirm Ready for Pricing"
      this.confirmModal.message = `${warning}Are you sure that this estimation is ready for pricing?`
      this.confirmModal.state = true;
    },
    markAsReadyForPricing() {
      this.updateEstimation({
        estimationId: this.estimation.id,
        data: { 'is_ready_for_pricing': true }
      }).then(() => {
        this.confirmModal.state = false;
        this.confirmModal.pending = false;
        this.wallSpreadsheet.updateTable(
          function (el, cell, x) { 
            cell.classList.add('readonly');
          }
        )
        this.railSpreadsheet.updateTable(
          function (el, cell, x) { 
            cell.classList.add('readonly'); 
          }
        )
      }).catch(() => this.confirmModal.pending = false)
    },
    confirmCompleted() {
      this.confirmModal.confirmCb = () => {
        this.confirmModal.pending = true;
        this.updateEstimation({
          estimationId: this.estimation.id,
          data: { 'is_completed': true }
        }).then(() => {
          this.confirmModal.state = false;
          this.confirmModal.pending = false;
        }).catch(() => this.confirmModal.pending = false)
      }
      this.confirmModal.title = "Confirm Estimation Completion"
      this.confirmModal.message = "Are you sure that this estimation is completed? \
        No further changes may be made after an estimation is marked as complete."
      this.confirmModal.state = true;
    },
    // confirmSalesApproval() {
    //   this.confirmModal.confirmCb = () => {
    //     this.updateEstimation({
    //       estimationId: this.estimation.id,
    //       data: { 'is_sales_approved': true }
    //     }).then(() => {
    //       this.confirmModal.state = false;
    //     })
    //   }
    //   this.confirmModal.title = "Confirm Sales Approval"
    //   this.confirmModal.message = "Are you sure you want to approve this estimation?"
    //   this.confirmModal.state = true;
    // },
    confirmClientApproval() {
      this.confirmModal.confirmCb = () => {
        this.confirmModal.pending = true;
        this.saveChanges().then(() => {
          this.updateEstimation({
            estimationId: this.estimation.id,
            data: { 'is_client_approved': true }
          }).then(() => {
            this.confirmModal.state = false;
            this.confirmModal.pending = false;
          }).catch(() => this.confirmModal.pending = false)
        }).catch(() => this.confirmModal.pending = false)
      }
      this.confirmModal.title = "Confirm Client Approval"
      this.confirmModal.message = "Are you sure you want to mark this estimation as \
      approved by the client? No further revisions can be made after client approval. \
      Note: Any unsaved changes will be automatically saved."
      this.confirmModal.state = true;
    },
    adjustPrice(section, row, v, activeAdjustment) {
      this.savingSummary = true;

      let payload = {
        summary_table: section,
        product_master_id: row.product_master_id,
        active_adjustment: activeAdjustment
      }

      let field = this.summaryParams.pricing_type == 'mpa' ? 
        'adjustment_unit_price_val' : 'adjustment_unit_price_dpa_val';

      payload[field] = v;

      this.adjustSummaryPrice({
        estimationId: this.estimation.id,
        data: payload
      }).then(
        () => this.savingSummary = false
      ).catch(() => this.savingSummary = false);
    },
    adjustBatteryPrice(v) {
      this.adjustSummaryPrice({
        estimationId: this.estimation.id,
        data: {
          summary_table: 'summary',
          pricing_summary: 'batteries and invertors',
          price: v
        }
      })
    },
    saveEstimationParams() {
      let data = {};
      let params = this.showParams.map(d => d.fields).flat(1);

      params.forEach(param => {
        data[param] = this.estimation[param]
      })
      this.updateEstimation({
        estimationId: this.estimation.id,
        data: data
      }).then(() => {
        this.adjustingParameters = false;
        this.savingParams = false;
        this.setNotification({
          status: true,
          type: 'success',
          message: 'Estimation successfully updated'
        });
        this.getSummary(this.estimation.id);
      }).catch(() => this.savingParams = false);
    },
    updateInsulationParam(insulationId, field, v, replaceWithResponse=true) {
      let payload = {}
      payload[field] = v;
      this.updatingInsulation = true;

      this.updateInsulation({
        insulationId: insulationId,
        data: payload,
        replaceWithResponse
      }).then(() => {
        this.updatingInsulation = false;
      }).catch(() => {
        this.updatingInsulation = false;
      })
    },
    setSaving(v) {
      this.saving = v;
    },
    saveChanges(wall, railing) {
      this.saving = true;
      
      if (this.estimation.rfq.rfq_type !== CONDENSED) {
        this.$refs.fullEstimation.saveFullEstimation(wall, railing);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.estimation__info {
  width: 100%;
  margin: 0em;
}
</style>
